'use client';

import type {
  EcomFooterProps,
  HeaderV3Props,
  LayoutV3Props,
  PdpHeaderProps,
} from '@jouzen/ecom-components';
import { PageLayoutV3 as Layout } from '@jouzen/ecom-components';

import usePageView from '@/analytics/hooks/usePageView';

import FooterMultivariatePaymentOptions from '../MultivariatePaymentOptions/FooterMultivariatePaymentOptions';
import { usePageLayout } from './hooks/usePageLayout';

export interface HeaderProps extends Partial<HeaderV3Props> {
  isCarouselInView?: PdpHeaderProps['isCarouselInView'];
}

export interface PageLayoutV3Props
  extends Omit<
    LayoutV3Props,
    | 'mainLabel'
    | 'footerProps'
    | 'headerProps'
    | 'eyebrowProps'
    | 'footnotesProps'
  > {
  readonly pageName: string;
  readonly footerProps?: Partial<EcomFooterProps>;
  readonly hasFooterMultivariatePayments?: boolean;
  readonly headerProps?: Partial<HeaderProps>;
  readonly hideFooter?: boolean;
  readonly hideHeader?: boolean;
  readonly preFooterContentProps?:
    | {
        wrapperClassName?: string;
      }
    | undefined;
}

const PageLayoutV3 = ({
  children,
  className = '',
  pageName,
  containerProps: parentContainerProps,
  footerProps: parentFooterProps,
  hasFooterMultivariatePayments = false,
  headerProps: parentHeaderProps,
  hideFooter = false,
  hideHeader = false,
  Header,
  preFooterContent,
  preFooterContentProps,
}: PageLayoutV3Props) => {
  const {
    containerProps,
    eyebrowProps,
    footnotesProps,
    headerProps,
    footerProps,
    observeNode,
  } = usePageLayout({
    parentContainerProps,
    parentFooterProps,
    parentHeaderProps,
  });

  usePageView(pageName);

  const _header = Header ? { Header } : undefined;

  return (
    <div data-cy="page-layout-v3" ref={observeNode}>
      <Layout
        className={className}
        containerProps={containerProps}
        eyebrowProps={eyebrowProps!}
        footnotesProps={footnotesProps!}
        footerProps={footerProps}
        headerProps={headerProps as HeaderV3Props}
        mainLabel="mainLabel"
        hideFooter={hideFooter}
        hideHeader={hideHeader}
        preFooterContent={
          <div className={preFooterContentProps?.wrapperClassName}>
            {preFooterContent}
            {hasFooterMultivariatePayments ? (
              <FooterMultivariatePaymentOptions />
            ) : null}
          </div>
        }
        {..._header}
      >
        {children}
      </Layout>
    </div>
  );
};

export default PageLayoutV3;
